import { useState } from 'react';
import { LiksChatComposer } from '../../components/liks/LiksChatComposer/LiksChatComposer';
import type { LiksChatComposerKnowledgeBaseOption } from '../../components/liks/LiksChatComposer/LiksChatComposer';
import { usePlatformChatStagedAttachments } from '../../features/chat/hooks/usePlatformChatStagedAttachments';
import { useTranscribePlatformChatAudioMutation } from '../../features/chat/mutations/usePlatformChatMutations';
import { useI18n } from '../../i18n/useI18n';
import { resolveApiErrorMessage } from '../../query/apiErrorMessage';
import { toastMutationError } from '../../query/mutationToasts';
import { toast } from '../../toast/toast';
import type { ChatMessageSendOptions } from '../../types/chatMessagesContext';
import { useUserConfig } from '../../userConfig/useUserConfig';
import { PlatformChatRagSettingsDialog } from './PlatformChatRagSettingsDialog';

type PlatformChatComposerAreaProps = {
  chatId: string;
  isSendingMessage: boolean;
  knowledgeBaseOptions: LiksChatComposerKnowledgeBaseOption[];
  isKnowledgeBaseMenuLoading: boolean;
  isLinkedKnowledgeBasesLoading: boolean;
  hasLinkedKnowledgeBases: boolean;
  onKnowledgeBaseToggle: (knowledgeBaseId: string) => void;
  onSendMessage: (
    content: string,
    options: ChatMessageSendOptions,
    attachmentIds: string[],
  ) => Promise<void>;
};

export function PlatformChatComposerArea({
  chatId,
  isSendingMessage,
  knowledgeBaseOptions,
  isKnowledgeBaseMenuLoading,
  isLinkedKnowledgeBasesLoading,
  hasLinkedKnowledgeBases,
  onKnowledgeBaseToggle,
  onSendMessage,
}: PlatformChatComposerAreaProps) {
  const { translate } = useI18n();
  const {
    replacePlatformChatDraftTextOnTranscription,
    platformChatMaxResults,
    platformChatMinScore,
    setPlatformChatRagSettings,
    isUserConfigReady,
  } = useUserConfig();
  const [isRagSettingsOpen, setIsRagSettingsOpen] = useState(false);
  const transcribeMutation = useTranscribePlatformChatAudioMutation();
  const {
    stagedAttachments,
    attachmentIds,
    hasUploadingAttachments,
    isDraftAttachmentsLoading,
    addFiles,
    removeAttachment,
    clearAttachments,
  } = usePlatformChatStagedAttachments(chatId);

  const isPersistedComposerContextReady =
    isUserConfigReady &&
    !isDraftAttachmentsLoading &&
    !isLinkedKnowledgeBasesLoading;

  const handleSend = async (
    content: string,
    options: ChatMessageSendOptions,
  ) => {
    if (hasUploadingAttachments) {
      return;
    }

    await onSendMessage(content, options, attachmentIds);
    clearAttachments();
  };

  const handleSendFailed = async ({
    error,
    failedText,
    restoredToComposer,
  }: {
    error: unknown;
    failedText: string;
    restoredToComposer: boolean;
  }) => {
    const apiMessage = await resolveApiErrorMessage(error);
    const description =
      apiMessage !== ''
        ? apiMessage
        : translate('platformChat.sendErrorDescription');

    if (restoredToComposer || failedText.trim() === '') {
      toast.error({
        title: translate('platformChat.sendErrorTitle'),
        description,
      });
      return;
    }

    toast.error({
      title: translate('platformChat.sendErrorTitle'),
      description,
      action: {
        label: translate('platformChat.copyFailedMessage'),
        onClick: () => {
          void navigator.clipboard.writeText(failedText);
        },
      },
    });
  };

  const handleTranscribe = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      transcribeMutation.mutate(file, {
        onSuccess: (text) => resolve(text),
        onError: (error) => {
          void toastMutationError(
            error,
            translate('platformChat.speechTranscribeErrorTitle'),
            translate('platformChat.speechTranscribeErrorDescription'),
          );
          reject(error);
        },
      });
    });

  return (
    <>
      <LiksChatComposer
        placeholder={translate('platformChat.inputPlaceholder')}
        attachLabel={translate('platformChat.attachDocument')}
        attachmentsMenuLabel={translate('platformChat.attachmentsMenuLabel')}
        attachmentsEmptyLabel={translate('platformChat.attachmentsEmpty')}
        uploadAttachmentLabel={translate('platformChat.uploadAttachment')}
        removeAttachmentLabel={translate('platformChat.removeAttachment')}
        uploadingAttachmentLabel={translate('platformChat.uploadingAttachment')}
        attachmentUploadFailedLabel={translate(
          'platformChat.attachmentUploadFailed',
        )}
        stagedAttachments={stagedAttachments}
        onAddFiles={addFiles}
        onRemoveStagedAttachment={removeAttachment}
        addLabel={translate('platformChat.add')}
        sendLabel={translate('platformChat.send')}
        clearLabel={translate('platformChat.clearInput')}
        webSearchLabel={translate('platformChat.webSearchLabel')}
        imageGenerationLabel={translate('platformChat.imageGenerationLabel')}
        imageGenerationMenuLabel={translate(
          'platformChat.imageGenerationMenuLabel',
        )}
        imageGenerationToggleLabel={translate(
          'platformChat.imageGenerationToggleLabel',
        )}
        imageFormatLabel={translate('platformChat.imageFormatLabel')}
        imageFormatLabels={{
          PNG: translate('platformChat.imageFormatPng'),
          JPEG: translate('platformChat.imageFormatJpeg'),
          WEBP: translate('platformChat.imageFormatWebp'),
        }}
        imageAspectRatioLabel={translate('platformChat.imageAspectRatioLabel')}
        imageAspectRatioLabels={{
          '1:1': translate('platformChat.imageAspectRatio1x1'),
          '16:9': translate('platformChat.imageAspectRatio16x9'),
          '9:16': translate('platformChat.imageAspectRatio9x16'),
          '3:4': translate('platformChat.imageAspectRatio3x4'),
        }}
        knowledgeBaseOptions={knowledgeBaseOptions}
        knowledgeBaseSearchPlaceholder={translate(
          'platformChat.knowledgeMenuSearchPlaceholder',
        )}
        knowledgeBaseSearchLabel={translate(
          'platformChat.knowledgeMenuSearchLabel',
        )}
        knowledgeBaseEmptyLabel={translate('platformChat.knowledgeMenuEmpty')}
        knowledgeBaseLoadingLabel={translate(
          'platformChat.knowledgeMenuLoading',
        )}
        knowledgeBaseMenuLabel={translate('platformChat.knowledgeMenuLabel')}
        exclusiveLockLabels={{
          attach: {
            becauseKnowledge: translate(
              'platformChat.exclusiveLock.attach.becauseKnowledge',
            ),
            becauseWebSearch: translate(
              'platformChat.exclusiveLock.attach.becauseWebSearch',
            ),
            becauseImageGeneration: translate(
              'platformChat.exclusiveLock.attach.becauseImageGeneration',
            ),
          },
          knowledge: {
            becauseAttachments: translate(
              'platformChat.exclusiveLock.knowledge.becauseAttachments',
            ),
            becauseWebSearch: translate(
              'platformChat.exclusiveLock.knowledge.becauseWebSearch',
            ),
            becauseImageGeneration: translate(
              'platformChat.exclusiveLock.knowledge.becauseImageGeneration',
            ),
          },
          webSearch: {
            becauseAttachments: translate(
              'platformChat.exclusiveLock.webSearch.becauseAttachments',
            ),
            becauseKnowledge: translate(
              'platformChat.exclusiveLock.webSearch.becauseKnowledge',
            ),
            becauseImageGeneration: translate(
              'platformChat.exclusiveLock.webSearch.becauseImageGeneration',
            ),
          },
          imageGeneration: {
            becauseAttachments: translate(
              'platformChat.exclusiveLock.imageGeneration.becauseAttachments',
            ),
            becauseKnowledge: translate(
              'platformChat.exclusiveLock.imageGeneration.becauseKnowledge',
            ),
            becauseWebSearch: translate(
              'platformChat.exclusiveLock.imageGeneration.becauseWebSearch',
            ),
          },
        }}
        searchClearLabel={translate('common.search.clear')}
        isKnowledgeBaseMenuLoading={isKnowledgeBaseMenuLoading}
        onKnowledgeBaseToggle={onKnowledgeBaseToggle}
        hasLinkedKnowledgeBases={hasLinkedKnowledgeBases}
        preferWebSearchDefault
        isPersistedComposerContextReady={isPersistedComposerContextReady}
        disabled={hasUploadingAttachments || !isUserConfigReady}
        sendDisabled={isSendingMessage || !isUserConfigReady}
        ragSettingsLabel={translate('platformChat.ragSettings.open')}
        onOpenRagSettings={() => {
          if (!isUserConfigReady) {
            return;
          }
          setIsRagSettingsOpen(true);
        }}
        speechInput={{
          replaceDraftText: replacePlatformChatDraftTextOnTranscription,
          onTranscribe: handleTranscribe,
          startLabel: translate('platformChat.speechInputStart'),
          stopLabel: translate('platformChat.speechInputStop'),
          recordingLabel: translate('platformChat.speechInputRecording'),
          onPermissionDenied: () => {
            toast.error({
              title: translate('platformChat.speechPermissionDeniedTitle'),
              description: translate(
                'platformChat.speechPermissionDeniedDescription',
              ),
            });
          },
          onMicrophoneUnavailable: () => {
            toast.error({
              title: translate('platformChat.speechMicrophoneUnavailableTitle'),
              description: translate(
                'platformChat.speechMicrophoneUnavailableDescription',
              ),
            });
          },
          onNoSpeechDetected: () => {
            toast.warning({
              title: translate('platformChat.speechNoSpeechDetectedTitle'),
            });
          },
        }}
        onSend={(content, options) => handleSend(content, options)}
        onSendFailed={handleSendFailed}
      />

      <PlatformChatRagSettingsDialog
        isOpen={isRagSettingsOpen}
        maxResults={platformChatMaxResults}
        minScore={platformChatMinScore}
        onCancel={() => setIsRagSettingsOpen(false)}
        onSave={({ maxResults, minScore }) => {
          setPlatformChatRagSettings({ maxResults, minScore });
          setIsRagSettingsOpen(false);
        }}
      />
    </>
  );
}
